// AiLys Partner Portal - Training page (Feature 10)
// Displays available courses, module progress, and certifications.
// Accessible by owner + agent roles.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortalLayout } from '../components/PortalLayout';
import { Card, CardLabel, Badge, EmptyState, SkeletonCard, colors, pageWrapperStyle, headingStyle, mutedStyle, linkStyle } from '../components/ui';
import { useLang, fmt } from '../i18n';

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  target_role: string;
  cert_level: string | null;
  estimated_hours: number | null;
  modules: CourseModule[];
}

interface CourseModule {
  id: string;
  module_id: string;
  sort_order: number;
  required: boolean;
  module: {
    id: string;
    slug: string;
    title: string;
    category: string;
    duration_minutes: number | null;
    content_url: string | null;
    quiz_questions: unknown[] | null;
    pass_threshold_pct: number;
  };
}

interface Completion {
  id: string;
  module_id: string;
  completed_at: string | null;
  quiz_score_pct: number | null;
  passed: boolean | null;
  attempts: number;
}

interface Certification {
  id: string;
  cert_level: string;
  issued_at: string;
  expires_at: string;
  status: string;
  course: { title: string } | null;
}

const CERT_LABELS: Record<string, string> = {
  cofounder_l1: 'Cofounder Level 1',
  cofounder_l2: 'Cofounder Level 2',
  cofounder_l3: 'Cofounder Level 3',
  agent_certified: 'Certified Agent',
};

const CATEGORY_BADGE_VARIANT: Record<string, 'info' | 'success' | 'purple' | 'warning'> = {
  product: 'info',
  sales: 'success',
  technical: 'purple',
  compliance: 'warning',
};

export default function Training() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [courses, setCourses] = useState<Course[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [certs, setCerts] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'courses' | 'certs'>('courses');
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/partner/training', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.courses) setCourses(data.courses as Course[]);
          if (data.completions) setCompletions(data.completions as Completion[]);
          if (data.certifications) setCerts(data.certifications as Certification[]);
        }
      } catch {
        // fail silently, empty state
      }
      setLoading(false);
    };
    load();
  }, []);

  const completionMap = useMemo(() => {
    const m = new Map<string, Completion>();
    completions.forEach(c => m.set(c.module_id, c));
    return m;
  }, [completions]);

  function getCourseProgress(course: Course) {
    const required = course.modules.filter(m => m.required);
    const done = required.filter(m => {
      const c = completionMap.get(m.module_id);
      return c?.passed === true;
    });
    return { total: required.length, done: done.length };
  }

  function formatDate(d: string) {
    return new Date(d).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }

  function daysUntil(d: string) {
    return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
  }

  if (loading) {
    return (
      <PortalLayout>
        <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </PortalLayout>
    );
  }

  const tabButtonBase = {
    padding: '0.5rem 1rem',
    border: '1px solid transparent',
    borderRadius: 6,
    cursor: 'pointer' as const,
    fontSize: '0.85rem',
    background: 'transparent',
    color: colors.muted,
    fontWeight: 400 as const,
  };

  const tabButtonActive = {
    ...tabButtonBase,
    background: colors.cyanBg,
    color: colors.cyan,
    border: `1px solid ${colors.cyanBorder}`,
    fontWeight: 600 as const,
  };

  return (
    <PortalLayout>
      <div style={{ ...pageWrapperStyle, maxWidth: 900, padding: '1rem', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <CardLabel>{t.training.title}</CardLabel>
            <h1 style={{ ...headingStyle, margin: 0 }}>{t.training.subtitle}</h1>
          </div>
          <a href="/dashboard" style={{ ...linkStyle, fontSize: '0.85rem' }}>
            {t.training.backLink}
          </a>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: `1px solid rgba(255,255,255,0.08)`, paddingBottom: '0.5rem' }}>
          <button
            onClick={() => setActiveTab('courses')}
            style={activeTab === 'courses' ? tabButtonActive : tabButtonBase}
          >
            {fmt(t.training.tabCourses, { count: courses.length })}
          </button>
          <button
            onClick={() => setActiveTab('certs')}
            style={activeTab === 'certs' ? tabButtonActive : tabButtonBase}
          >
            {fmt(t.training.tabCertifications, { count: certs.length })}
          </button>
        </div>

        {/* Courses tab */}
        {activeTab === 'courses' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {courses.length === 0 && (
              <EmptyState
                title={t.training.tabCourses.replace(/\s*\(.*\)/, '')}
                description={t.training.noCourses}
              />
            )}
            {courses.map(course => {
              const progress = getCourseProgress(course);
              const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
              const isExpanded = expandedCourse === course.id;

              return (
                <Card key={course.id} style={{ overflow: 'hidden', padding: 0 }}>
                  {/* Course header */}
                  <button
                    onClick={() => setExpandedCourse(isExpanded ? null : course.id)}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: 'none',
                      border: 'none',
                      color: colors.text,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{course.title}</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: 4 }}>
                          {course.cert_level && (
                            <Badge variant="info" style={{ fontSize: '0.7rem' }}>
                              {CERT_LABELS[course.cert_level] || course.cert_level}
                            </Badge>
                          )}
                          {course.estimated_hours && (
                            <span style={{ fontSize: '0.75rem', color: colors.muted }}>
                              {fmt(t.training.hours, { hours: course.estimated_hours })}
                            </span>
                          )}
                          <span style={{ fontSize: '0.75rem', color: colors.muted }}>
                            {fmt(t.training.modules, { count: course.modules.length })}
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 80 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: pct === 100 ? colors.successAlt : colors.text }}>
                          {fmt(t.training.progress, { pct })}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: colors.muted }}>
                          {fmt(t.training.moduleDone, { done: progress.done, total: progress.total })}
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ marginTop: 8, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? colors.successAlt : colors.cyan, borderRadius: 2, transition: 'width 0.3s' }} />
                    </div>
                  </button>

                  {/* Expanded module list */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '0.5rem 1rem 1rem' }}>
                      {course.description && (
                        <p style={{ ...mutedStyle, margin: '0 0 0.75rem' }}>{course.description}</p>
                      )}
                      {course.modules.map((cm, idx) => {
                        const mod = cm.module;
                        const completion = completionMap.get(cm.module_id);
                        const isDone = completion?.passed === true;
                        const badgeVariant = CATEGORY_BADGE_VARIANT[mod.category] || 'neutral' as const;

                        return (
                          <div
                            key={cm.id}
                            onClick={() => mod.slug && navigate(`/training/${mod.slug}`)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' && mod.slug) navigate(`/training/${mod.slug}`); }}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.6rem 0',
                              borderBottom: idx < course.modules.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                              cursor: 'pointer',
                              borderRadius: 4,
                            }}
                          >
                            {/* Status icon */}
                            <div style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              flexShrink: 0,
                              background: isDone ? colors.successBgLight : 'rgba(255,255,255,0.06)',
                              color: isDone ? colors.successAlt : colors.mutedLight,
                            }}>
                              {isDone ? '✓' : idx + 1}
                            </div>

                            {/* Module info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{mod.title}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: 2 }}>
                                <Badge variant={badgeVariant} style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase' }}>
                                  {mod.category}
                                </Badge>
                                {mod.duration_minutes && (
                                  <span style={{ fontSize: '0.7rem', color: colors.mutedLight }}>
                                    {fmt(t.training.duration, { duration: mod.duration_minutes })}
                                  </span>
                                )}
                                {!cm.required && (
                                  <span style={{ fontSize: '0.65rem', color: colors.mutedLight, fontStyle: 'italic' }}>{t.training.optional}</span>
                                )}
                              </div>
                            </div>

                            {/* Score */}
                            {completion && (
                              <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                                {completion.quiz_score_pct !== null && (
                                  <div style={{ color: isDone ? colors.successAlt : colors.dangerAlt }}>
                                    {completion.quiz_score_pct}%
                                  </div>
                                )}
                                <div style={{ color: colors.mutedLight }}>
                                  {fmt(t.training.attempts, { count: completion.attempts })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Certifications tab */}
        {activeTab === 'certs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {certs.length === 0 && (
              <EmptyState
                title={t.training.tabCertifications.replace(/\s*\(.*\)/, '')}
                description={t.training.noCerts}
              />
            )}
            {certs.map(cert => {
              const remaining = daysUntil(cert.expires_at);
              const badgeVariant = cert.status === 'active' ? 'success'
                : cert.status === 'renewed' ? 'info'
                : cert.status === 'revoked' ? 'neutral'
                : 'danger' as const;

              return (
                <Card key={cert.id}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                        {CERT_LABELS[cert.cert_level] || cert.cert_level}
                      </h3>
                      {cert.course && (
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: colors.muted }}>
                          {(cert.course as { title: string }).title}
                        </p>
                      )}
                    </div>
                    <Badge variant={badgeVariant} style={{ textTransform: 'uppercase' }}>
                      {cert.status}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.75rem', fontSize: '0.8rem', color: colors.muted }}>
                    <span>{fmt(t.training.issued, { date: formatDate(cert.issued_at) })}</span>
                    <span>{fmt(t.training.expires, { date: formatDate(cert.expires_at) })}</span>
                    {cert.status === 'active' && remaining <= 30 && (
                      <span style={{ color: remaining <= 7 ? colors.dangerAlt : colors.warningAlt, fontWeight: 600 }}>
                        {fmt(t.training.daysRemaining, { days: remaining })}
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
